import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ExternalLink, Send, ThumbsUp, MessageSquareWarning } from 'lucide-react';

interface Resp {
  demanda?: { id: string; titulo: string; pauta?: string; status: string; linkFinal?: string };
  solicitante?: { nome: string };
  error?: string;
}

function getApiUrl(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
}

export function AprovarPublico() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<'loading' | 'ok' | 'err' | 'aprovado' | 'revisao_enviada'>('loading');
  const [resp, setResp] = useState<Resp | null>(null);
  const [errMsg, setErrMsg] = useState('');
  const [feedback, setFeedback] = useState('');
  const [nomeAprovador, setNomeAprovador] = useState('');
  const [mostrarFeedback, setMostrarFeedback] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${getApiUrl()}/aprovacao-publica/${token}`)
      .then(async (r) => {
        const d = await r.json() as Resp;
        if (!r.ok) {
          setErrMsg(d.error === 'invalid_token' ? 'Este link expirou ou é inválido.' : 'Peça não encontrada.');
          setState('err'); return;
        }
        setResp(d);
        setState('ok');
      })
      .catch(() => { setErrMsg('Erro de conexão. Tente novamente em alguns minutos.'); setState('err'); });
  }, [token]);

  async function enviar(acao: 'aprovar' | 'pedir-revisao') {
    if (!token) return;
    if (acao === 'pedir-revisao' && !feedback.trim()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${getApiUrl()}/aprovacao-publica/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao, feedback: feedback.trim() || undefined, nomeAprovador: nomeAprovador.trim() || undefined }),
      });
      const d = await r.json();
      if (!r.ok) { setErrMsg(d.error || 'Erro ao enviar.'); setState('err'); return; }
      setState(acao === 'aprovar' ? 'aprovado' : 'revisao_enviada');
    } catch { setErrMsg('Erro de conexão.'); setState('err'); }
    finally { setSubmitting(false); }
  }

  if (state === 'loading') {
    return <Wrapper><Loader2 className="w-8 h-8 animate-spin text-white/60 mx-auto" /></Wrapper>;
  }

  if (state === 'err') {
    return <Wrapper>
      <div className="text-center">
        <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-3" />
        <h1 className="text-xl font-bold mb-2">Ops</h1>
        <p className="text-sm text-white/70">{errMsg}</p>
        <p className="text-xs text-white/40 mt-4">Fale com a Apex se precisar de um novo link.</p>
      </div>
    </Wrapper>;
  }

  if (state === 'aprovado') {
    return <Wrapper>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Peça aprovada!</h1>
        <p className="text-sm text-white/70 mb-1">A equipe Apex foi notificada e vai publicar em breve.</p>
        <p className="text-xs text-white/50">Pode fechar esta página.</p>
      </div>
    </Wrapper>;
  }

  if (state === 'revisao_enviada') {
    return <Wrapper>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
          <MessageSquareWarning className="w-10 h-10 text-purple-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Pedido enviado</h1>
        <p className="text-sm text-white/70 mb-1">Seu feedback chegou à Apex. Eles vão ajustar e te mandam uma nova versão.</p>
      </div>
    </Wrapper>;
  }

  const d = resp?.demanda;
  if (!d) return null;

  return (
    <Wrapper>
      <div className="space-y-5">
        <div className="text-center mb-2">
          <div className="text-[10px] uppercase tracking-widest text-blue-300 font-semibold mb-1">Apex Research · aprovação</div>
          {resp?.solicitante && <div className="text-xs text-white/50">para {resp.solicitante.nome}</div>}
        </div>

        <div>
          <h1 className="text-2xl font-bold mb-2">{d.titulo}</h1>
          {d.pauta && <p className="text-sm text-white/70 leading-relaxed">{d.pauta}</p>}
        </div>

        {d.linkFinal && (
          <a href={d.linkFinal} target="_blank" rel="noreferrer"
            className="block bg-white/5 hover:bg-white/10 border border-white/15 rounded-lg p-4 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300">
                <ExternalLink className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Ver a peça</div>
                <div className="text-[11px] text-white/50 truncate">{d.linkFinal}</div>
              </div>
            </div>
          </a>
        )}

        <div className="bg-white/5 border border-white/15 rounded-lg p-4">
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold block mb-1.5">Seu nome (opcional)</label>
          <input type="text" value={nomeAprovador} onChange={e => setNomeAprovador(e.target.value)}
            placeholder="Pra registrarmos quem aprovou"
            className="w-full text-sm bg-white/10 border border-white/15 rounded-md px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:border-blue-400" />
        </div>

        {!mostrarFeedback ? (
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => enviar('aprovar')} disabled={submitting}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-3.5 rounded-lg inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              <ThumbsUp className="w-4 h-4" /> Aprovar
            </button>
            <button type="button" onClick={() => setMostrarFeedback(true)}
              className="bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold py-3.5 rounded-lg inline-flex items-center justify-center gap-2 transition-colors">
              <MessageSquareWarning className="w-4 h-4" /> Pedir ajustes
            </button>
          </div>
        ) : (
          <div className="bg-white/5 border border-purple-400/30 rounded-lg p-4 space-y-3">
            <label className="text-[10px] uppercase tracking-wider text-purple-300 font-bold block">O que precisa ajustar?</label>
            <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={4} autoFocus
              placeholder="Conta o que precisa mudar..."
              className="w-full text-sm bg-white/10 border border-white/15 rounded-md px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 resize-none" />
            <div className="flex gap-2">
              <button type="button" onClick={() => { setMostrarFeedback(false); setFeedback(''); }} className="px-4 py-2 text-xs text-white/60 hover:text-white">Cancelar</button>
              <button type="button" onClick={() => enviar('pedir-revisao')} disabled={!feedback.trim() || submitting}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" />
                {submitting ? 'Enviando…' : 'Enviar pedido'}
              </button>
            </div>
          </div>
        )}

        <p className="text-[10.5px] text-white/40 text-center pt-3">
          Este link é único e pode ser usado apenas uma vez.<br />Apex Research · {new Date().getFullYear()}
        </p>
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1020] via-[#001B3A] to-[#0A2540] text-white py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-[#0a1020]/70 backdrop-blur-sm border border-white/10 rounded-2xl p-7 shadow-2xl">
          <div className="flex items-center justify-center mb-5">
            <div className="w-10 h-10 rounded-lg bg-blue-500 relative">
              <div className="absolute inset-2 bg-white" />
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
